import { Observable, OperatorFunction } from 'rxjs';
import {
  GraphBuilderContext,
  GraphInputDefinition,
  GraphNodeBuilder,
  IGraphBuilder,
  OperatorNodeDefinition,
} from './graph-builder.types';

/**
 * Function to create an operator node
 */
export type OperatorNodeFactoryFn<T = any, R = any> = (
  def: OperatorNodeDefinition<any>,
  ctx: GraphBuilderContext,
  rootFactory: IGraphBuilder & GraphNodeBuilder,
) => OperatorFunction<T, R> | undefined;

/**
 * Function to create a node
 */
type NodeFactoryFn<T = any> = (
  def: GraphInputDefinition<any>,
  ctx: GraphBuilderContext,
  rootFactory: IGraphBuilder & GraphNodeBuilder,
) => Observable<T> | undefined;

/**
 * Basic abstract implementation of a graph node builder.
 *
 * - Add source factories using `addSource`
 * - Add operators using `addOperator`
 * - implement `createFakeDefinitions` if needed
 *
 * @example
 * // Define operators and sources
 * enum OperatorName {
 *  MyOperator = 'my-operator'
 * }
 *
 * enum NodeName {
 *  Node = 'my-node'
 * }
 *
 * // extend AbstractGraphNodeBuilder
 * class Provider extends AbstractGraphNodeBuilder<
 *  NodeName, OperatorName
 * > {
 *  constructor() {
 *    // Provide sources
 *    this.addSource(NodeName.Node, ...);
 *
 *    // Provide operators
 *    this.addOperator(OperatorName.MyOperator, ...);
 *  }
 * }
 *
 */
export abstract class AbstractGraphNodeBuilder<
  TNodes extends string,
  TOperatorNodes extends string,
> implements GraphNodeBuilder {
  /**
   * Registry for the provided operator functions
   */
  protected readonly operatorMap = new Map<
    TOperatorNodes,
    OperatorNodeFactoryFn
  >();
  /**
   * Registry for the provided source factories
   */
  protected readonly sourceMap = new Map<TNodes, NodeFactoryFn>();

  /**
   * Adds an operator with a given `name`.
   *
   * @param name The name of the operator
   * @param fn The operator
   */
  protected addOperator(name: TOperatorNodes, fn: OperatorNodeFactoryFn): this {
    this.operatorMap.set(name, fn);
    return this;
  }

  /**
   * Adds a node factory with a given `name`.
   *
   * @param name The name of the node
   * @param fn Factory function
   */
  protected addNode(name: TNodes, fn: NodeFactoryFn): this {
    this.sourceMap.set(name, fn);
    return this;
  }

  parseSource(
    def: GraphInputDefinition,
    ctx: GraphBuilderContext,
    rootFactory: IGraphBuilder,
  ): Observable<any> | undefined {
    const { name } = def;

    const sourceFn = this.sourceMap.get(name as TNodes);
    return sourceFn?.(def, ctx, rootFactory);
  }

  parseOperator(
    def: OperatorNodeDefinition,
    ctx: GraphBuilderContext,
    factory: IGraphBuilder,
  ): OperatorFunction<any, any> | undefined {
    const { operator } = def;

    const operatorFn = this.operatorMap.get(operator as TOperatorNodes);
    return operatorFn?.(def, ctx, factory);
  }
}
